import { reflect } from '@effector/reflect';
import { NumberInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { PenSquare } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { Jackpot, USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm, ModalFormActionIconProps, ModalFormProps } from 'shared/ui';
import { EditJackpotModel } from '../../model';

export const createEditJackpotModal = (model: EditJackpotModel) => {
	const ModalForm = createModalForm(model);

	const BetRateField = reflect({
		view: NumberInput,
		bind: {
			'data-test-id': 'edit-jackpot-bet-rate-input',
			label: translated('edit-jackpot.fields.rate.label'),
			placeholder: translated('edit-jackpot.fields.rate.placeholder'),
			error: combine(
				[model.form.fields.betRate.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-jackpot.rate'),
					}),
			),
			value: model.form.fields.betRate.$value,
			onChange: (value) => model.form.fields.betRate.change(String(value ?? '')),
			required: true,
			rightSection: '%',
		},
	});

	const InitialAmountField = reflect({
		view: NumberInput,
		bind: {
			'data-test-id': 'edit-jackpot-initial-amount-input',
			label: translated('edit-jackpot.fields.initial.label'),
			placeholder: translated('edit-jackpot.fields.initial.placeholder'),
			error: combine(
				[model.form.fields.initialAmount.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-jackpot.initial'),
					}),
			),
			value: model.form.fields.initialAmount.$value,
			onChange: (value) => model.form.fields.initialAmount.change(String(value ?? '')),
			required: true,
			rightSection: model.$modalData.map((jackpot) => jackpot?.currency ?? null),
		},
	});

	const LimitAmountField = reflect({
		view: NumberInput,
		bind: {
			'data-test-id': 'edit-jackpot-limit-amount-input',
			label: translated('edit-jackpot.fields.limit.label'),
			placeholder: translated('edit-jackpot.fields.limit.placeholder'),
			error: combine(
				[model.form.fields.limitAmount.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-jackpot.fields.limit.label'),
						secondFieldName: t('edit-jackpot.fields.initial.label'),
					}),
			),
			value: model.form.fields.limitAmount.$value,
			onChange: (value) => model.form.fields.limitAmount.change(String(value ?? '')),
			required: true,
			rightSection: model.$modalData.map((jackpot) => jackpot?.currency ?? null),
		},
	});

	const EditJackpotModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [pending, pristine, closed] = useUnit([
			model.editJackpotMutation.$pending,
			model.form.$pristine,
			model.modalClosed,
		]);

		return (
			<ModalForm
				ref={ref}
				title={t('edit-jackpot.title')}
				cancelButtonProps={{
					children: t('edit-jackpot.cancel'),
				}}
				submitButtonProps={{
					children: t('edit-jackpot.save'),
					loading: pending,
					disabled: pristine,
				}}
				onExitTransitionEnd={closed}
				{...props}
			>
				<BetRateField />
				<InitialAmountField />
				<LimitAmountField />
			</ModalForm>
		);
	});

	const EditJackpotModalActionIcon = forwardRef<
		HTMLButtonElement,
		ModalFormActionIconProps<Jackpot>
	>(({ modalData, ...props }, ref) => {
		const role = useUnit(viewerModel.$role);

		return (
			<ModalForm.ActionIcon
				ref={ref}
				data-test-id='edit-jackpot-action-icon'
				variant='ghost-secondary'
				modalData={modalData}
				disabled={role !== USER_ROLES.god}
				{...props}
			>
				<PenSquare />
			</ModalForm.ActionIcon>
		);
	});

	return Object.assign(EditJackpotModal, {
		ActionIcon: EditJackpotModalActionIcon,
	});
};
